/**
 * Redeem request service.
 * Reads via Supabase RLS-gated SELECT; writes via /api/redeem/* (service-role).
 */
import { supabase } from '../lib/supabase';
import { apiFetch } from './api-client';
import { normalizeAddress } from '../lib/shop-address';
import { getCollectibleForRedemption, type AwardedCollectible } from './collection-service';

export interface RedeemRequestRow {
  id: string;
  customer_address: string;
  customer_name: string | null;
  shop_id: string;
  shop_owner_address: string;
  nft_id: string;
  nft_level: number;
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'stuck';
  onchain_tx_hash: string | null;
  onchain_status: 'none' | 'submitting' | 'confirmed' | 'failed';
  created_at: string;
  responded_at: string | null;
  confirmed_at: string | null;
  expires_at: string;
}

// createRedeemRequest/approveRedeemRequest/denyRedeemRequest (the pre-tap
// voucher queue + owner-approval flow) deleted 2026-08-01 — the counter spec
// replaced them with the single-call redeem() action, and their server
// endpoints ('create', 'respond') no longer exist in api/redeem.ts.

export async function updateRedeemOnchainStatus(
  requestId: string,
  onchainStatus: 'submitting' | 'confirmed' | 'failed',
  txHash?: string,
): Promise<void> {
  try {
    await apiFetch('/api/redeem', { action: 'onchain-status', requestId, onchainStatus, txHash });
  } catch (err: any) {
    console.error('updateRedeemOnchainStatus failed:', err?.message || err);
  }
}

export async function getPendingRedeemsForShop(shopOwnerAddress: string): Promise<RedeemRequestRow[]> {
  const { data, error } = await supabase
    .from('redeem_requests')
    .select('*')
    .eq('shop_owner_address', normalizeAddress(shopOwnerAddress))
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data;
}

/**
 * Resolves the collectible a just-confirmed burn awarded, isolated from the
 * caller's own burn try/catch and guarded against staleness.
 *
 * Isolation: getCollectibleForRedemption already swallows its own errors,
 * but that is an implementation detail of a different module. If it ever
 * starts throwing (or is replaced), a lookup failure must still never
 * propagate into the caller's burn try/catch and mark a SUCCESSFUL burn as
 * failed — the coffee is already handed over; only the on-screen reveal may
 * be lost.
 *
 * Staleness: `isStillActive` is evaluated AFTER the (possibly slow) lookup
 * resolves, not before — a burn can confirm after the counter modal has
 * closed or been superseded by a newly opened redemption. Only reveal what
 * still belongs on screen.
 */
export async function resolveConfirmedReveal(
  redemptionId: string,
  isStillActive: () => boolean,
): Promise<AwardedCollectible | null> {
  try {
    const awarded = await getCollectibleForRedemption(redemptionId);
    return awarded && isStillActive() ? awarded : null;
  } catch (err: any) {
    console.error('resolveConfirmedReveal failed:', err?.message || err);
    return null;
  }
}

export async function getStuckRedeems(customerAddress: string): Promise<RedeemRequestRow[]> {
  // Approved redeems whose NFT burn never confirmed — 'none' (customer missed
  // the realtime approval event), 'submitting' (tx interrupted), 'failed'
  // (retry candidates). Mirror of getStuckStamps.
  const { data, error } = await supabase
    .from('redeem_requests')
    .select('*')
    .eq('customer_address', customerAddress)
    .eq('status', 'approved')
    .in('onchain_status', ['none', 'submitting', 'failed'])
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data;
}
